import type { Messages } from '../messages.js'

/**
 * French. `coups` for flips: the literal `retournements` is accurate and four times too long
 * for a meter, and a currency of actions is a `coup` in every French game there is.
 */
export const fr: Messages = {
  tag: 'fr',

  readingDictionary: 'Lecture du dictionnaire…',
  noWordList: 'Aucune liste de mots pour « {language} ». Générez-la :  pnpm dictionary build',
  emptyWordList: 'La liste de mots pour « {language} » est vide.',

  flips: 'coups',
  score: 'points',
  words: 'mots',
  round: 'manche',
  ticksLeftLabel: 'Temps restant dans la manche',
  typeAWord: 'tapez un mot',
  tapPrompt: 'touchez pour prendre ou rendre, puis {action}',

  boardOfTiles: 'Plateau de {n} tuiles',
  faceDown: 'face cachée',
  wildCard: 'joker',
  wildKey: 'une lettre au choix',
  letterReplaced: '{from} est devenu {to}',
  letterSwap: 'CHANGEMENT DE LETTRE !',
  spentTile: 'tuile utilisée',
  hiddenWhilePaused: 'masquée pendant la pause',
  letterInWord: '{letter}, lettre {position} du mot',

  completeWord: 'Valider le mot',

  completeShort: 'Valider',
  reset: 'Effacer',
  pause: 'Pause',
  resume: 'Reprendre',
  newGame: 'Nouvelle partie',
  paused: 'En pause',
  outOfFlips: 'Plus de coups',
  finalResult: '{score} points, {words} en {rounds}',
  playAgain: 'Rejouer',
  share: 'Partager',
  shareCopied: 'Copié.',
  shareSelect: 'Copiez ceci :',

  lettersSelect: 'les lettres sélectionnent',
  keysWild: 'prise quand vous tapez une lettre qu’aucune tuile ne montre',
  clearsEvery: 'retire tous les {letter} sélectionnés',
  undoLastLetter: 'annule la dernière lettre',
  noWordsYet: 'Aucun mot pour le moment.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'déjà trouvé',
  reasonTooShort: 'trop court',
  reasonNotAWord: "n'est pas un mot",
  reasonAllFound: 'vous les avez déjà tous',
  noSuchLetterUp: 'aucun {letter} retourné',
  nothingUp: 'rien de retourné',
  letterHidden: 'une lettre s’est cachée',
  shuffled: 'mélangé',
  shuffledAndBilled: 'mélangé, {flips} coups inutilisés facturés',
  tooFewLetters: 'Trop peu de lettres restantes',

  gameLanguage: 'langue',
  interfaceLanguage: 'interface',
  dictionarySize: '{common} courants sur {full} mots',
  filterLanguages: 'Rechercher une langue',
  noMatches: 'Aucun résultat',

  nerdMode: 'mode expert',
  rules: 'Règles',
  difficulty: 'difficulté',
  difficultyNames: { easy: 'facile', medium: 'moyen', hard: 'difficile', insane: 'infernal' },
  pausedNoBoard: 'Cette partie n’apparaîtra pas au classement.',
  about: 'À propos',
  themeLabel: 'thème',
  themeNames: { traditional: 'traditionnel', light: 'clair', contrast: 'contraste élevé' },
  tiles: 'tuiles (N)',
  secondsPerTick: 'secondes / temps',
  holdTicks: 'temps de maintien',
  minWord: 'mot minimum',
  startingFlips: 'coups au départ',
  wildChance: 'probabilité de joker',
  replaceChance: 'chance de changement de lettre',
  hideChance: 'chance de masquage de lettre',
  wordCompleteMode: 'mot validé',
  wordCompleteNames: { shuffle: 'mélanger', spend: 'consommer', keep: 'conserver' },
  flipEconomy: 'économie des coups',
  flipEconomyNames: {
    none: 'aucune',
    perLetter: 'par lettre',
    fibonacci: 'fibonacci',
    overMinimum: 'au-delà du minimum',
  },
  repeatedLetterKey: 'touche lettre répétée',
  keySchemeNames: { cycle: 'cycler', advance: 'avancer' },
  keySchemeHelp: {
    cycle:
      'A prend le prochain A libre, et une fois tous dans le mot, les retire. ' +
      'Maj+A les retire aussi.',
    advance: 'A prend le prochain A libre. Maj+A retire tous les A du mot.',
  },

  whatThatMeans: 'Ce que cela donne',
  factRound: 'une manche',
  factWholeBoardUp: 'plateau entier visible',
  factRoundCosts: 'une manche coûte',
  factFlipsBuy: 'les coups initiaux valent',
  factThisBoard: 'ce plateau',
  factBoardHadToAdmit: 'le plateau devait admettre',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, le plus long {longest}',
  wordsIncludingOneOf: '{words} dont un de {ceiling}',
  scorelessRounds: '{rounds} sans marquer',

  whatAWordPays: 'Ce que rapporte un mot',
  columnLetters: 'lettres',
  columnCost: 'coût',
  columnPoints: 'points',
  columnFlips: 'coups',
  columnNet: 'net',

  canonicalRules: 'Règles {difficulty} officielles.',
  customRules: 'Réglages modifiés. Les scores sur règles personnalisées ne sont pas classés.',
  applyAndStart: 'Appliquer et lancer une partie',
  changesNextGame: 'Les changements prennent effet à la prochaine partie.',
  presets: 'Préréglages :',

  start: 'Commencer',
  restart: 'Recommencer',
  quit: 'Quitter',
  quitTitle: 'Quitter cette partie ?',
  restartTitle: 'Recommencer cette partie ?',
  restartConfirm: 'Recommencer',
  quitConfirm: 'Quitter',
  keepPlaying: 'Continuer',
  personalBest: 'Vos meilleures parties',
  newPersonalBest: 'Nouveau record personnel.',
  columnRank: '#',
  notRanked: 'Règles personnalisées : cette partie n’est pas classée.',
  notRankedPaused: 'L’horloge s’est arrêtée : cette partie n’est pas classée.',
  rankOfTotal: '{rank} sur {total}',

  howToPlay: 'Comment jouer',

  backToGame: 'Retour au jeu',
  welcomeTitle: 'Bienvenue dans Blinkered',
  tutorialSkip: 'Passer',
  tutorialNext: 'Suivant',
  tutorialBack: 'Retour',
  tutorialStart: 'Commencer à jouer',
  tutorialHideAgain: 'Ne plus afficher',
  tutorialProgress: '{n} sur {total}',
  tutorialSkipTitle: 'Passer la visite ?',
  tutGoalTitle: 'Blinkered est une course',
  tutGoalBody:
    'Vous commencez la partie avec un nombre fixe de coups, et chaque tuile qui se retourne coûte un coup. Quand les coups sont épuisés, la partie est terminée. Les mots rapportent des coups, et plus le mot est long, plus vous en gagnez.',
  tutSpentBody:
    'Les lettres utilisées dans un mot restent face cachée jusqu’à la manche suivante. Seul un changement de lettre modifie les lettres présentes sur le plateau.',
  tutChangeTitle: 'Les lettres peuvent changer',
  tutSameLetters:
    'Vous avez repéré un mot sans pouvoir le jouer ? Les mêmes lettres reviennent à la manche suivante, mélangées à de nouvelles places.',
  tutPickLetters: 'Touchez les lettres voulues, dans l’ordre, pour former un mot.',
  tutMoreTurn:
    'Les tuiles continuent de se retourner pendant que vous réfléchissez : une meilleure lettre peut encore arriver.',
  tutTapBack:
    "Vous en avez pris une par erreur ? Touchez-la de nouveau pour la rendre. N'importe laquelle, pas seulement la dernière.",
  tutComplete: 'Appuyez sur Terminer quand le mot est prêt.',
  htBoardTitle: 'Le plateau',
  htBoardBody:
    'Les tuiles se retournent une à une, dans un ordre aléatoire. Les tuiles visibles servent à former des mots.',
  htWordsTitle: 'Les mots',
  htWordsBody:
    'Composez un mot avec les tuiles visibles, en tapant ou en cliquant les lettres dans l’ordre.',
  htFlipsTitle: 'Les coups',
  htFlipsBody:
    'Chaque tuile retournée coûte un coup. Un mot terminé rend des coups à votre total, et les mots longs en rendent davantage. Quand les coups sont épuisés, la partie est terminée.',
  htRoundTitle: 'La manche',
  htRoundBody:
    'Quand la dernière tuile d’une manche se retourne, tout le plateau reste visible un instant. Puis les tuiles sont retournées face cachée et mélangées, et une nouvelle manche commence.',
  htLanguagesTitle: 'Les langues',
  htLanguagesBody:
    '{n} langues. Chaque plateau peut être résolu avec des mots réellement employés. Un mot rare compte quand même, si le dictionnaire le connaît.',
  htKeysTitle: 'Le clavier',
  htWildTitle: 'Les jokers',
  htWildBody:
    'Parfois un joker apparaît à la place d’une lettre. Un joker vaut n’importe quelle lettre qui forme un mot valide. Un mot déjà terminé ne compte pas.',
  htHideTitle: 'Lettres qui se cachent',
  htHideBody:
    'Aux niveaux les plus difficiles, une lettre visible peut se retourner d’elle-même et revenir plus tard dans la manche. Le minuteur gagne un cran pour cela, en rouge. Les lettres déjà prises ne se retournent pas.',
  tutHideBody:
    'Aux niveaux les plus difficiles, une lettre visible peut se retourner d’elle-même et revenir plus tard dans la manche. Les lettres déjà prises ne se retournent pas.',
  htSwapTitle: 'Les lettres changent',
  htSwapBody:
    'Parfois, entre deux manches, une lettre est remplacée par une autre. Vous verrez quelle lettre est partie et laquelle est arrivée.',
  htLevelsTitle: 'Les niveaux',
  htLevelEasy:
    'Les mêmes douze lettres pendant toute la partie : vous pouvez les apprendre et garder une liste de mots en tête. Les tuiles se retournent lentement et le plateau complet reste visible assez longtemps pour finir de choisir vos lettres.',
  htLevelMedium:
    'Une lettre change de temps en temps, ce qui rend plus difficile de retenir les mots que vous comptiez jouer plus tard. Moins de temps pour regarder, et moins pour réfléchir.',
  htLevelHard:
    "Les mots de trois lettres ne comptent plus, et une lettre change environ une manche sur deux. Le plateau est à peine visible qu'il se remélange déjà.",
  htLevelInsane:
    'Tout à la fois, à toute vitesse. Le plateau est mélangé presque aussitôt après le dernier retournement.',
  htTouchTitle: 'L’écran tactile',
  htTouchBody:
    'Touchez une tuile retournée pour prendre sa lettre. Touchez une lettre déjà prise pour la rendre. Valider et Effacer sont sous le plateau.',

  accountTitle: 'Votre compte',
  menuProfile: 'Mon profil',
  menuGames: 'Mes parties',
  menuPublicPage: 'Ma page publique',
  menuSignedInAs: 'Connecté en tant que',
  signIn: 'Se connecter',
  signOut: 'Se déconnecter',
  signInTitle: 'Se connecter ou s’inscrire',
  signInLead: 'Un compte conserve vos parties, sur tous vos appareils.',
  signInKeepGame: 'Connectez-vous pour conserver cette partie, et toutes les suivantes.',
  continueWithApple: 'Continuer avec Apple',
  continueWithGoogle: 'Continuer avec Google',
  signInOr: 'ou',
  emailLabel: 'E-mail',
  codeLabel: 'Code',
  emailMeACode: 'Envoyez-moi un code',
  working: 'En cours…',
  sendNewCode: 'Envoyer un nouveau code',
  codeSent: 'Consultez votre boîte mail. Le code est valable 10 minutes.',
  notNow: 'Pas maintenant',
  badEmail: 'Cela ne ressemble pas à une adresse e-mail.',
  badCode:
    'Ce code n’a pas fonctionné. Les codes expirent après 10 minutes et ne servent qu’une fois.',
  serverBusy: 'Serveur injoignable. Réessayez dans un instant.',
  serverDown: 'Serveur injoignable.',
  offline: 'Hors ligne',
  leaderboardTitle: 'Classement',
  leaderboardEmpty: 'Aucune partie dans ce classement pour l’instant. Sois le premier.',
  leaderboardWouldBe: 'Où se classerait cette partie',
  leaderboardThisGame: 'Cette partie',
  leaderboardRanksHere: 'Où se classe cette partie',
  leaderboardOutside: 'Hors des cinq premières.',
  tutAccountTitle: 'Gardez vos scores',
  tutAccountBody:
    'Un compte enregistre vos parties, place vos scores au classement et conserve votre profil.',
  ssoCancelled: 'Connexion annulée.',
  ssoExpired: 'Cela a pris trop de temps. Reconnectez-vous.',
  ssoNoUsername: 'Impossible de terminer la création du compte. Réessayez dans un instant.',
  ssoFailed: 'Cela n’a pas fonctionné. Réessayez, ou utilisez votre e-mail.',
  tabProfile: 'Profil',
  countryAny: 'Je préfère ne pas dire',
  findCountry: 'Chercher un pays',
  usernameLabel: 'Nom d’utilisateur',
  bioLabel: 'Bio',
  countryLabel: 'Pays',
  bioHint: 'Pas de liens. {left}/{max}',
  save: 'Enregistrer',
  saving: 'Enregistrement…',
  saved: 'Enregistré.',
  nameTaken: 'Ce nom est déjà pris.',
  nameTooShort: 'Un peu plus long, s’il vous plaît.',
  nameTooLong: 'C’est trop long.',
  nameBadCharacters: 'Lettres, chiffres, tirets et tirets bas uniquement.',
  nameBadEdges: 'Il doit commencer et finir par une lettre ou un chiffre.',
  nameMixedScripts: 'Choisissez un seul alphabet et tenez-vous-y.',
  nameReserved: 'Ce nom ressemble à ceux que nous attribuons. Choisissez-en un autre.',
  nameUnusable: 'Ce nom ne peut pas être utilisé.',
  bioTooLong: 'Les bios font {max} caractères au maximum.',
  bioHasLink: 'Les liens ne sont pas autorisés dans une bio.',
  bioHasControl: 'Cela contient des caractères qu’une bio ne peut pas accueillir.',
  bioUnusable: 'Cette bio ne peut pas être utilisée.',
  gamesHeading: 'Parties',
  gamesEmpty: 'Rien ici pour l’instant. Les parties jouées en étant connecté apparaîtront ici.',
  gamesEmptyShort: 'Rien ici pour l’instant.',
  gamesLoading: 'Lecture de vos parties…',
  theirGamesLoading: 'Lecture de leurs parties…',
  profileLoading: 'Lecture du profil…',
  playerNotFound: 'Personne ne porte ce nom ici.',
  columnWhen: 'Quand',
  columnRounds: 'manches',
  columnGame: 'Partie',
  gameLoading: 'Lecture de la partie…',
  gameUnreadable: 'Cette partie n’a pas pu être lue.',
  gameNotRanked: 'Jouée avec des règles modifiées, cette partie n’est pas classée.',
  detailNotKept: 'Les mots et les grilles de cette partie ne sont plus conservés.',
  openingBoard: 'La grille de départ',
  roundByRound: 'Tour par tour',
  roundNothing: 'rien trouvé',
  changeReplaced: '{letter} remplacé en case {slot}',
  changeWildOn: '{letter} joker en case {slot}',
  changeWildOff: '{letter} joker retiré de la case {slot}',
  keepThisGame: 'Conserver cette partie',
  savedToYourGames: 'Enregistrée dans vos parties.',

  reportThis: 'Signaler',
  reportHeading: 'Signaler ceci',
  reportTheName: 'Le nom',
  reportTheBio: 'La description',
  reportTheScore: 'Le score',
  reportWhy: 'Qu’est-ce qui ne va pas ? Facultatif.',
  reportSend: 'Envoyer le signalement',
  reportSent: 'Merci. Quelqu’un va y jeter un œil.',
  reportSignIn: 'Connectez-vous pour signaler quelque chose.',
  reportGone: 'Ce n’est plus là.',
  reportCancel: 'Annuler',

  deleteAccount: 'Supprimer mon compte',
  deleteAccountWhat:
    'Cela supprime votre profil et toutes les parties que vous avez conservées, définitivement. C’est irréversible.',
  deleteAccountAsk: 'Envoyez-moi un code',
  deleteConfirm: 'Supprimer définitivement',
  deleteGone: 'Votre compte n’existe plus. Merci d’avoir joué.',
  deleteNoAddress:
    'Aucune adresse confirmée sur ce compte, donc une suppression ne peut pas être vérifiée. Contactez-nous et nous le ferons à la main.',

  rulesetFacts: 'Mots de {letters} · une nouvelle tuile toutes les {seconds}',
  plurals: {
    words: { one: '{n} mot', other: '{n} mots' },
    rounds: { one: '{n} manche', other: '{n} manches' },
    flips: { one: '{n} coup', other: '{n} coups' },
    ticks: { one: '{n} temps', other: '{n} temps' },
    points: { one: '{n} point', other: '{n} points' },
    minLetters: { one: '{n} lettre ou plus', other: '{n} lettres ou plus' },
    seconds: { one: '{n} seconde', other: '{n} secondes' },
  },
}
